import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import Swal from "sweetalert2";
import { Button, Modal, Form } from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const UPLOADS_BASE = (API_URL || "").replace(/\/api\/?$/, "") || API_URL;

const SalaryOfficerTable = () => {
  const [data, setData] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [officer, setOfficer] = useState(null);
  const [balance, setBalance] = useState({ available_balance: 0, total_allocated: 0, total_paid: 0 });
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [payrollForm, setPayrollForm] = useState({ payroll_type: "", payroll_account: "" });
  const [requestAmount, setRequestAmount] = useState("");
  const [requestAmountDisplay, setRequestAmountDisplay] = useState("");
  const [showProofModal, setShowProofModal] = useState(false);
  const [selectedProof, setSelectedProof] = useState(null);

  const fetchData = async () => {
    const token = localStorage.getItem("token");
    const decoded = jwtDecode(token);
    const officer_id = decoded.officer_id;
    if (!officer_id) return;

    try {
      const [officerRes, balanceRes, requestsRes, salariesRes] = await Promise.all([
        axios.get(`${API_URL}/api/officers/${officer_id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/disbursements/balance/${officer_id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/disbursements/officer/${officer_id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/salaries/officer/${officer_id}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setOfficer(officerRes.data);
      setPayrollForm({
        payroll_type: officerRes.data.payroll_type || "",
        payroll_account: officerRes.data.payroll_account || "",
      });
      setBalance(balanceRes.data);
      setData(requestsRes.data);
      setSalaries(salariesRes.data || []);
    } catch (err) {
      console.error("Failed to fetch data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSavePayroll = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${API_URL}/api/officers/me/payroll`, payrollForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      Swal.fire("Berhasil", "Data payroll berhasil disimpan", "success");
      setShowPayrollModal(false);
      fetchData();
    } catch (err) {
      Swal.fire("Gagal", err.response?.data?.error || "Gagal menyimpan", "error");
    }
  };

  const unformatRupiah = (str) => parseInt(String(str).replace(/\D/g, ""), 10) || 0;
  const formatRupiahInput = (num) => {
    if (!num) return "";
    return Math.floor(num).toLocaleString("id-ID");
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    const amountNum = unformatRupiah(requestAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      Swal.fire("Gagal", "Masukkan jumlah yang valid", "error");
      return;
    }
    if (amountNum > balance.available_balance) {
      Swal.fire("Gagal", `Jumlah melebihi limit (maks Rp ${balance.available_balance.toLocaleString("id-ID")})`, "error");
      return;
    }
    if (!payrollForm.payroll_type || !payrollForm.payroll_account) {
      Swal.fire("Gagal", "Lengkapi payroll type dan payroll account terlebih dahulu", "error");
      return;
    }

    const confirm = await Swal.fire({
      title: "Ajukan Pencairan?",
      html: `Nominal: <strong>Rp ${amountNum.toLocaleString("id-ID")}</strong><br/>Ke: ${payrollForm.payroll_type} - ${payrollForm.payroll_account}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Ya, ajukan",
    });

    if (confirm.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        await axios.post(`${API_URL}/api/disbursements`, { amount: amountNum }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        Swal.fire("Berhasil", "Ajuan pencairan berhasil dikirim", "success");
        setShowRequestModal(false);
        setRequestAmount("");
        setRequestAmountDisplay("");
        fetchData();
      } catch (err) {
        Swal.fire("Gagal", err.response?.data?.error || "Gagal mengajukan", "error");
      }
    }
  };

  const payrollTypeLabel = (v) => (v === "bank" ? "Bank" : v === "e_wallet" ? "E-Wallet" : v || "-");

  const formatRupiah = (value) => {
    const v = parseFloat(value);
    if (isNaN(v)) return "Rp 0";
    return `Rp ${Math.floor(v).toLocaleString("id-ID")}`;
  };

  const handleViewProof = (row) => {
    if (row.attachment) {
      setSelectedProof(`${UPLOADS_BASE}/uploads/${row.attachment}`);
      setShowProofModal(true);
    } else {
      Swal.fire("Info", "Belum ada bukti transfer", "info");
    }
  };

  const columns = [
    { name: "Nominal", selector: (row) => formatRupiah(row.amount), sortable: true },
    { name: "Payroll", selector: (row) => `${payrollTypeLabel(row.payroll_type)} - ${row.payroll_account}` },
    {
      name: "Status",
      cell: (row) => (
        <span className={`badge ${row.status === "paid" ? "bg-success" : "bg-warning"}`}>
          {row.status === "paid" ? "Paid" : "Pending"}
        </span>
      ),
    },
    { name: "Tanggal Ajuan", selector: (row) => new Date(row.request_date).toLocaleDateString("id-ID") },
    {
      name: "Tanggal Bayar",
      selector: (row) => (row.paid_date ? new Date(row.paid_date).toLocaleDateString("id-ID") : "-"),
    },
    {
      name: "Aksi",
      cell: (row) =>
        row.status === "paid" && row.attachment ? (
          <Button variant="outline-primary" size="sm" onClick={() => handleViewProof(row)}>
            Lihat Bukti TF
          </Button>
        ) : (
          "-"
        ),
    },
  ];

  return (
    <div>
      <h2>Pencairan Gaji</h2>

      {/* Card: Gaji yang tersedia */}
      <div className="card mb-4 border-primary">
        <div className="card-body text-center py-4">
          <h6 className="text-muted mb-2">Gaji kamu yang tersedia</h6>
          <h3 className="mb-0 text-primary fw-bold">{formatRupiah(Math.max(0, balance.total_allocated - balance.total_paid))}</h3>
          {balance.total_tasks > 0 && balance.completion_percentage < 100 && (
            <div className="mt-3 text-warning fw-bold" style={{ fontSize: '0.9rem' }}>
              Ayo selesaikan semua task kamu untuk mengklaim 100% gaji kamu. Semangat! 🚀
            </div>
          )}
        </div>
      </div>

      {/* Payroll & Balance Section */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h6 className="text-muted mb-1">Payroll Type & Account</h6>
              <p className="mb-0">
                {payrollTypeLabel(officer?.payroll_type)} | {officer?.payroll_account || "-"}
              </p>
              <Button variant="link" size="sm" className="p-0" onClick={() => setShowPayrollModal(true)}>
                Edit
              </Button>
            </div>
            <div>
              <h6 className="text-muted mb-1">Limit Pencairan (Tersedia)</h6>
              <p className="mb-0 fw-bold text-primary">{formatRupiah(balance.available_balance)}</p>
              <small className="text-muted">
                Total alokasi: {formatRupiah(balance.total_allocated)} | Sudah dicairkan: {formatRupiah(balance.total_paid)}
              </small>
              {balance.total_tasks > 0 && (
                <div className="mt-1">
                  <span className="badge bg-info text-dark">
                    Beban Task: {balance.approved_tasks} / {balance.total_tasks} selesai ({balance.completion_percentage}%)
                  </span>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>
                    Limit maksimal Anda adalah {balance.completion_percentage}% dari total alokasi
                  </div>
                </div>
              )}
            </div>
            <Button
              variant="primary"
              disabled={balance.available_balance <= 0}
              onClick={() => setShowRequestModal(true)}
            >
              Ajukan Pencairan
            </Button>
          </div>
        </div>
      </div>

      {/* Riwayat Pemasukan Gaji */}
      <DataTable
        title="Riwayat Pemasukan Gaji"
        columns={[
          { name: "Jumlah", selector: (row) => formatRupiah(row.amount), sortable: true },
          { name: "Deskripsi", selector: (row) => row.description || "-" },
          { name: "Tanggal Input", selector: (row) => new Date(row.input_date).toLocaleDateString("id-ID") },
        ]}
        data={salaries}
        pagination
        highlightOnHover
        responsive
        noDataComponent="Belum ada data pemasukan gaji."
        className="mb-5"
      />

      {/* Disbursement Requests Table */}
      <DataTable
        title="Riwayat Pengajuan Pencairan"
        columns={columns}
        data={data}
        progressPending={loading}
        pagination
        highlightOnHover
        responsive
        noDataComponent="Belum ada pengajuan pencairan."
      />

      {/* Payroll Edit Modal */}
      <Modal show={showPayrollModal} onHide={() => setShowPayrollModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Payroll</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSavePayroll}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Payroll Type</Form.Label>
              <Form.Select
                value={payrollForm.payroll_type}
                onChange={(e) => setPayrollForm((p) => ({ ...p, payroll_type: e.target.value }))}
              >
                <option value="">-- Pilih --</option>
                <option value="bank">Bank</option>
                <option value="e_wallet">E-Wallet</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Payroll Account</Form.Label>
              <Form.Control
                type="text"
                value={payrollForm.payroll_account}
                onChange={(e) => setPayrollForm((p) => ({ ...p, payroll_account: e.target.value }))}
                placeholder="Contoh: BRI : 9802139139 atau DANA : 082200990022"
              />
              <Form.Text className="text-muted">
                Tulis nama bank/ewallet lalu : nomor rekening/HP. Contoh: BRI : 9802139139 atau DANA : 082200990022
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPayrollModal(false)}>Batal</Button>
            <Button type="submit" variant="primary">Simpan</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Request Disbursement Modal */}
      <Modal show={showRequestModal} onHide={() => { setShowRequestModal(false); setRequestAmount(""); setRequestAmountDisplay(""); }}>
        <Modal.Header closeButton>
          <Modal.Title>Ajukan Pencairan (Open Amount)</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleRequest}>
          <Modal.Body>
            <p className="text-muted">Maksimal: {formatRupiah(balance.available_balance)}</p>
            {balance.total_tasks > 0 && (
                <div className="alert alert-info py-2" style={{ fontSize: '0.85rem' }}>
                    Limit pencairan Anda disesuaikan dengan persentase Task yang telah diapprove ({balance.approved_tasks}/{balance.total_tasks} = {balance.completion_percentage}%).
                </div>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Nominal (Rp)</Form.Label>
              <Form.Control
                type="text"
                value={requestAmountDisplay}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, "");
                  setRequestAmount(raw);
                  setRequestAmountDisplay(raw ? formatRupiahInput(parseInt(raw, 10)) : "");
                }}
                placeholder="Contoh: 5.000.000"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowRequestModal(false); setRequestAmount(""); setRequestAmountDisplay(""); }}>Batal</Button>
            <Button type="submit" variant="primary" disabled={unformatRupiah(requestAmount) <= 0}>
              Ajukan
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Proof Modal */}
      <Modal show={showProofModal} onHide={() => setShowProofModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Bukti Transfer</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedProof && (
            <>
              <a href={selectedProof} target="_blank" rel="noopener noreferrer">
                <img src={selectedProof} alt="Bukti Transfer" style={{ maxWidth: "100%", maxHeight: "70vh" }} />
              </a>
              <p className="mt-2">
                <a href={selectedProof} target="_blank" rel="noopener noreferrer">Buka di tab baru</a>
              </p>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SalaryOfficerTable;
